import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useAuth } from "../context/authContext";
import { hasRoleAccess, ACCESS } from "../utils/rolePermissions";
import gameScoreService from "../api/gameScoreService";

const STYLES = `
  .crk-root {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    min-height: 140px;
    max-height: 240px;
    background: #ffffff;
    border-radius: 16px;
    border: 1.5px solid #e4e7ed;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(15,17,23,0.06), 0 1px 4px rgba(15,17,23,0.04);
    font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
  }
  .crk-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 12px 8px;
    border-bottom: 1.5px solid #f0f2f6;
    background: linear-gradient(180deg, #fafbfd 0%, #ffffff 100%);
    flex-shrink: 0;
  }
  .crk-title { font-size: 12px; font-weight: 700; color: #0f1117; }
  .crk-count { font-size: 11px; color: #9ba3b5; }
  .crk-list {
    flex: 1;
    overflow: auto;
    min-height: 0;
    scrollbar-width: thin;
    scrollbar-color: #d1d5e0 transparent;
  }
  .crk-list::-webkit-scrollbar { width: 6px; }
  .crk-list::-webkit-scrollbar-thumb { background: #d1d5e0; border-radius: 99px; }
  .crk-row {
    display: grid;
    grid-template-columns: 28px 1fr auto;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-bottom: 1px solid #f0f2f6;
  }
  .crk-row.is-me {
    background: #eef5fd;
  }
  .crk-pos {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-size: 11px;
    font-weight: 800;
    color: #5a6175;
    background: #f2f4f8;
  }
  .crk-pos.is-1 { background: #fef3c7; color: #b45309; }
  .crk-pos.is-2 { background: #e5e7eb; color: #4b5563; }
  .crk-pos.is-3 { background: #ffedd5; color: #c2410c; }
  .crk-name {
    font-size: 12px;
    font-weight: 600;
    color: #0f1117;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .crk-you { font-weight: 500; color: #1975d1; margin-left: 4px; }
  .crk-score {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1px;
  }
  .crk-pts {
    font-size: 12px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: #0f1117;
  }
  .crk-plays {
    font-size: 10px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: #9ba3b5;
  }
  .crk-empty {
    padding: 22px 12px;
    text-align: center;
    font-size: 12px;
    color: #9ba3b5;
  }
`;

function userKey(user) {
  return String(user?._id || user?.id || "");
}

export default function CockroachRanking({ refreshKey = 0, game = "cockroach" }) {
  const { effectiveUser, role } = useAuth();
  const isAdmin = hasRoleAccess(role, ACCESS.ADMIN_ONLY);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const me = userKey(effectiveUser);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    gameScoreService
      .getLeaderboard(game)
      .then((res) => {
        if (!alive) return;
        setRows(res.data?.data || []);
      })
      .catch(() => {
        if (!alive) return;
        setRows([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [refreshKey, game]);

  return (
    <Box className="crk-root">
      <style>{STYLES}</style>
      <div className="crk-head">
        <span className="crk-title">{game === "memorama" ? "Ranking lotería" : "Ranking"}</span>
        <span className="crk-count">
          {loading ? "…" : `${rows.length} jugador${rows.length === 1 ? "" : "es"}`}
        </span>
      </div>
      <div className="crk-list">
        {!loading && rows.length === 0 && (
          <div className="crk-empty">Aún nadie ha jugado</div>
        )}
        {rows.map((row, index) => {
          const pos = index + 1;
          const mine = row.userId === me;
          return (
            <div key={row.userId} className={`crk-row${mine ? " is-me" : ""}`}>
              <span className={`crk-pos${pos <= 3 ? ` is-${pos}` : ""}`}>{pos}</span>
              <span className="crk-name">
                {row.name}
                {mine && <span className="crk-you">tú</span>}
              </span>
              <span className="crk-score">
                <span className="crk-pts">{row.bestScore}</span>
                {isAdmin && (
                  <span className="crk-plays">
                    {row.gamesPlayed || 0} intento{(row.gamesPlayed || 0) === 1 ? "" : "s"}
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </Box>
  );
}
